import { useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { translate } from 'react-i18nify';
import moment from 'moment';
import Modal from '../../../Modal';
import Select from '../../../Select';
import RadioButton from '../../../RadioButton';
import api from '../../../../api/apiClient';
import styles from './CreateSupportErrandModal.module.scss';

type Props = {
  visible: boolean;
  onClose: () => void;
  authToken: string;
  patient: { guid: string };
  showNotification: (message: string, type: string) => void;
};

type TypeOption = { label: string; value: SupportErrandType };

const i18n = 'member_details.support_errand';

const RECIPIENTS: { value: SupportErrandRecipient; labelKey: string }[] = [
  { value: 'PAL', labelKey: `${i18n}.recipient_PAL` },
  { value: 'PAS', labelKey: `${i18n}.recipient_PAS` }
];

const translateType = (type: string) => {
  const key = `${i18n}.type.${type.toLowerCase()}`;
  const translated = translate(key);
  return translated === key ? type : translated;
};

const toOption = (t: SupportErrandType): TypeOption => ({
  label: translateType(t.type),
  value: t
});

const buildGroupedOptions = (errandTypes: SupportErrandType[]) => {
  const groups: { label: string; options: TypeOption[] }[] = [];

  const palOnly = errandTypes
    .filter((e) => e.validRecipients.length === 1 && e.validRecipients[0] === 'PAL')
    .map(toOption)
    .sort((a, b) => a.label.localeCompare(b.label));

  if (palOnly.length) {
    groups.push({ label: translate(`${i18n}.group_pal`), options: palOnly });
  }

  const pasOnly = errandTypes
    .filter((e) => e.validRecipients.length === 1 && e.validRecipients[0] === 'PAS')
    .map(toOption)
    .sort((a, b) => a.label.localeCompare(b.label));

  if (pasOnly.length) {
    groups.push({ label: translate(`${i18n}.group_pas`), options: pasOnly });
  }

  const multi = errandTypes
    .filter((e) => e.validRecipients.length > 1 && !e.validRecipients.includes('COPS'))
    .map(toOption)
    .sort((a, b) => a.label.localeCompare(b.label));

  if (multi.length) {
    groups.push({ label: translate(`${i18n}.group_multi`), options: multi });
  }

  return groups;
};

const CreateSupportErrandModal = ({ visible, onClose, authToken, patient, showNotification }: Props) => {
  const [selectedType, setSelectedType] = useState<SupportErrandType | undefined>();
  const [selectedRecipient, setSelectedRecipient] = useState<SupportErrandRecipient | undefined>();
  const [message, setMessage] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const { data: errandTypes = [] } = useSWR<SupportErrandType[]>(visible ? '/clinic/support-errand-type' : null);

  const availableRecipients = RECIPIENTS.filter((r) => selectedType?.validRecipients?.includes(r.value));

  useEffect(() => {
    if (!selectedType) return;
    const available = RECIPIENTS.filter((r) => selectedType.validRecipients?.includes(r.value));
    if (available.length === 1) {
      setSelectedRecipient(available[0].value);
    }
  }, [selectedType]);

  const reset = () => {
    setSelectedType(undefined);
    setSelectedRecipient(undefined);
    setMessage('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!selectedType || !selectedRecipient || !patient?.guid) {
      return;
    }

    setIsCreating(true);

    try {
      const res = await api.createSupportErrand(authToken, patient.guid, selectedType.type, selectedRecipient, message);

      if (res.ok) {
        showNotification(translate(`${i18n}.success`), 'success');
        mutate(`/clinic/support-errands/${patient.guid}`);
        handleClose();
      } else {
        showNotification(translate(`${i18n}.error`), 'error');
      }
    } catch {
      showNotification(translate(`${i18n}.error`), 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const groupedOptions = buildGroupedOptions(errandTypes);
  const messageRequired = selectedType?.type !== 'CONTACT_PATIENT';
  const isComplete = !!selectedType && !!selectedRecipient && (!messageRequired || message.trim().length > 0);

  return (
    <Modal
      header={translate(`${i18n}.modal_header`)}
      actionI18nKey="global.buttons.send"
      visible={visible}
      onClose={handleClose}
      onActionCompleted={handleCreate}
      actionCompletable={isComplete}
      actionCompleting={isCreating}
      size="medium"
    >
      <div className="stack-1">
        <div>{translate(`${i18n}.description`)}</div>

        <div>
          <h4>{translate(`${i18n}.errand_type`)}</h4>
          <Select
            placeholder={translate(`${i18n}.choose_type`)}
            isSearchable={false}
            onChange={(option: TypeOption) => {
              setSelectedType(option.value);
              setSelectedRecipient(undefined);
            }}
            classNamePrefix="single-select"
            options={groupedOptions}
          />
        </div>

        {selectedType && availableRecipients.length > 0 && (
          <div>
            <h4>{translate(`${i18n}.recipient`)}</h4>
            <div className={styles.recipients}>
              {availableRecipients.map((r) => (
                <RadioButton
                  key={r.value}
                  name="errand-recipient"
                  label={translate(r.labelKey)}
                  checked={selectedRecipient === r.value}
                  onChange={() => setSelectedRecipient(r.value)}
                  disabled={availableRecipients.length === 1}
                />
              ))}
            </div>
          </div>
        )}

        <div>
          <h4>{translate(`${i18n}.message`)}</h4>
          <textarea
            className={styles.messageInput}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={translate(`${i18n}.message_placeholder`)}
          />
        </div>

        <ErrandHistory patientId={patient?.guid} visible={visible} />
      </div>
    </Modal>
  );
};

function ErrandHistory({ patientId, visible }: { patientId: string; visible: boolean }) {
  const { data: errands = [], error } = useSWR<SupportErrand[]>(
    visible && patientId ? `/clinic/support-errands/${patientId}` : null
  );

  return (
    <div className={styles.history}>
      <h4>{translate(`${i18n}.history_header`)}</h4>

      {error ? (
        <div>{translate(`${i18n}.history_error`)}</div>
      ) : !errands.length ? (
        <div className="grey-700">{translate(`${i18n}.history_empty`)}</div>
      ) : (
        <div>
          <div className={styles.errandHeaderRow}>
            <div>{translate(`${i18n}.column_type`)}</div>
            <div>{translate(`${i18n}.column_recipient`)}</div>
            <div>{translate(`${i18n}.column_created`)}</div>
            <div>{translate(`${i18n}.column_status`)}</div>
            <div />
          </div>
          <div className="stack-1">
            {errands.map((errand, i) => (
              <ErrandRow key={i} errand={errand} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ErrandRow({ errand }: { errand: SupportErrand }) {
  const [expanded, setExpanded] = useState(false);

  const senderName = errand.createdBy ? `${errand.createdBy.givenName} ${errand.createdBy.familyName}` : null;

  return (
    <div className={styles.errandItem}>
      <div className={styles.errandRow} onClick={() => setExpanded(!expanded)}>
        <div>{translateType(errand.type)}</div>
        <div>{errand.recipient ?? '-'}</div>
        <div>{moment(errand.createdAt).format('YYYY-MM-DD')}</div>
        <div>
          <span className={errand.isOpen ? styles.statusOpen : styles.statusClosed}>
            {errand.isOpen ? translate(`${i18n}.status_open`) : translate(`${i18n}.status_closed`)}
          </span>
        </div>
        <div className={styles.chevron} data-expanded={expanded}>
          ▾
        </div>
      </div>

      {expanded && (
        <div className={styles.errandDetail}>
          {errand.description && (
            <div className={styles.errandMessage}>
              <div className={styles.errandMessageHeader}>
                {senderName && <span className="fw-600">{senderName}</span>}
                <span className="grey-700">{moment(errand.createdAt).format('HH:mm · YYYY-MM-DD')}</span>
              </div>
              <div>{errand.description}</div>
            </div>
          )}
          {errand.messages?.length > 0 &&
            errand.messages.map((msg: any, j: number) => (
              <div key={j} className={styles.errandMessage}>
                <div className={styles.errandMessageHeader}>
                  {msg.sender && (
                    <span className="fw-600">
                      {msg.sender.givenName} {msg.sender.familyName}
                    </span>
                  )}
                  <span className="grey-700">{moment(msg.createdAt).format('HH:mm · YYYY-MM-DD')}</span>
                </div>
                <div>{msg.message}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default CreateSupportErrandModal;
